import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";

type FloatingPosition = {
  x: number;
  y: number;
};

type FloatingAxis = "x" | "y";

type DragState = {
  pointerId: number;
  startX: number;
  startY: number;
  pointerOffsetX: number;
  pointerOffsetY: number;
  dragging: boolean;
};

type FloatingRecord = {
  instanceKey: symbol;
  id: string;
  storageKey: string;
  stackIndex: number;
  element: HTMLButtonElement | null;
  position: FloatingPosition;
  width: number;
  height: number;
  setPosition: (position: FloatingPosition) => void;
};

type UseDraggableFloatingOptions = {
  id?: string;
  stackIndex?: number;
};

const DRAG_THRESHOLD = 4;
const MOBILE_BREAKPOINT = 640;
const MOBILE_SIZE = 48;
const DESKTOP_SIZE = 56;
const MOBILE_VIEWPORT_PADDING = 12;
const DESKTOP_VIEWPORT_PADDING = 24;
const MOBILE_GAP = 12;
const DESKTOP_GAP = 14;
const EPSILON = 0.01;

const floatingRegistry = new Map<symbol, FloatingRecord>();
const dirtyRecords = new Set<symbol>();
let activeDragInstance: symbol | null = null;
let layoutFrame: number | null = null;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function getViewportMetrics() {
  const desktop =
    typeof window !== "undefined" && window.innerWidth >= MOBILE_BREAKPOINT;
  return {
    padding: desktop ? DESKTOP_VIEWPORT_PADDING : MOBILE_VIEWPORT_PADDING,
    gap: desktop ? DESKTOP_GAP : MOBILE_GAP,
    expectedSize: desktop ? DESKTOP_SIZE : MOBILE_SIZE,
  };
}

function getDefaultPosition(
  stackIndex: number,
  width?: number,
  height?: number,
): FloatingPosition {
  if (typeof window === "undefined") return { x: 0, y: 0 };

  const metrics = getViewportMetrics();
  const resolvedWidth = width || metrics.expectedSize;
  const resolvedHeight = height || metrics.expectedSize;
  return {
    x: Math.max(
      metrics.padding,
      window.innerWidth - resolvedWidth - metrics.padding,
    ),
    y: Math.max(
      metrics.padding,
      window.innerHeight -
        resolvedHeight -
        metrics.padding -
        Math.max(0, stackIndex) * (resolvedHeight + metrics.gap),
    ),
  };
}

function clampToViewport(
  position: FloatingPosition,
  width: number,
  height: number,
): FloatingPosition {
  if (typeof window === "undefined") return position;

  const { padding } = getViewportMetrics();
  return {
    x: clamp(position.x, padding, window.innerWidth - width - padding),
    y: clamp(position.y, padding, window.innerHeight - height - padding),
  };
}

function getActiveRecords(): FloatingRecord[] {
  return Array.from(floatingRegistry.values()).filter(
    (record) => record.element && record.width > 0 && record.height > 0,
  );
}

function applyRecordPosition(
  record: FloatingRecord,
  position: FloatingPosition,
  markDirty = true,
) {
  if (
    Math.abs(record.position.x - position.x) < EPSILON &&
    Math.abs(record.position.y - position.y) < EPSILON
  ) {
    return;
  }

  record.position = position;
  record.setPosition(position);
  if (markDirty) dirtyRecords.add(record.instanceKey);
}

function flushDirtyPositions() {
  // Dragged locations remain in memory for this page session only.
  dirtyRecords.clear();
}

function recordsOverlap(
  first: FloatingRecord,
  second: FloatingRecord,
  gap = getViewportMetrics().gap,
): boolean {
  return (
    first.position.x < second.position.x + second.width + gap &&
    first.position.x + first.width + gap > second.position.x &&
    first.position.y < second.position.y + second.height + gap &&
    first.position.y + first.height + gap > second.position.y
  );
}

function boundaryShiftLimit(
  record: FloatingRecord,
  axis: FloatingAxis,
  direction: -1 | 1,
): number {
  if (typeof window === "undefined") return 0;
  const { padding } = getViewportMetrics();

  if (axis === "x") {
    return direction > 0
      ? Math.max(0, window.innerWidth - padding - record.width - record.position.x)
      : Math.max(0, record.position.x - padding);
  }

  return direction > 0
    ? Math.max(0, window.innerHeight - padding - record.height - record.position.y)
    : Math.max(0, record.position.y - padding);
}

function requiredPushDistance(
  source: FloatingRecord,
  neighbor: FloatingRecord,
  axis: FloatingAxis,
  direction: -1 | 1,
): number {
  const { gap } = getViewportMetrics();

  if (axis === "x") {
    if (direction > 0) {
      return Math.max(
        0,
        source.position.x + source.width + gap - neighbor.position.x,
      );
    }
    return Math.max(
      0,
      neighbor.position.x + neighbor.width + gap - source.position.x,
    );
  }

  if (direction > 0) {
    return Math.max(
      0,
      source.position.y + source.height + gap - neighbor.position.y,
    );
  }
  return Math.max(
    0,
    neighbor.position.y + neighbor.height + gap - source.position.y,
  );
}

function shiftRecordAlongAxis(
  record: FloatingRecord,
  axis: FloatingAxis,
  direction: -1 | 1,
  requestedDistance: number,
  visited: Set<symbol>,
): number {
  if (requestedDistance <= EPSILON) return 0;

  const start = { ...record.position };
  let shifted = Math.min(
    requestedDistance,
    boundaryShiftLimit(record, axis, direction),
  );
  if (shifted <= EPSILON) return 0;

  const nextPosition = { ...start };
  nextPosition[axis] += direction * shifted;
  applyRecordPosition(record, nextPosition);

  const nextVisited = new Set(visited);
  nextVisited.add(record.instanceKey);

  for (let pass = 0; pass < floatingRegistry.size + 1; pass += 1) {
    let adjusted = false;
    for (const neighbor of getActiveRecords()) {
      if (
        neighbor.instanceKey === record.instanceKey ||
        nextVisited.has(neighbor.instanceKey) ||
        !recordsOverlap(record, neighbor)
      ) {
        continue;
      }

      const required = requiredPushDistance(record, neighbor, axis, direction);
      if (required <= EPSILON) continue;

      const pushed = shiftRecordAlongAxis(
        neighbor,
        axis,
        direction,
        required,
        nextVisited,
      );
      if (pushed + EPSILON < required) {
        shifted = Math.max(0, shifted - (required - pushed));
        const backedOff = { ...start };
        backedOff[axis] += direction * shifted;
        applyRecordPosition(record, backedOff);
      }
      adjusted = true;
    }

    if (!adjusted) break;
  }

  return shifted;
}

function moveRecordToward(
  record: FloatingRecord,
  target: FloatingPosition,
) {
  const clampedTarget = clampToViewport(
    target,
    record.width,
    record.height,
  );
  const deltaX = clampedTarget.x - record.position.x;
  const deltaY = clampedTarget.y - record.position.y;
  const axes: Array<{ axis: FloatingAxis; delta: number }> = [
    { axis: "x", delta: deltaX },
    { axis: "y", delta: deltaY },
  ].sort((left, right) => Math.abs(right.delta) - Math.abs(left.delta));

  for (const { axis, delta } of axes) {
    if (Math.abs(delta) <= EPSILON) continue;

    const direction: -1 | 1 = delta > 0 ? 1 : -1;
    let remaining = Math.abs(delta);
    const maxStep = Math.max(8, Math.min(record.width, record.height) / 3);
    while (remaining > EPSILON) {
      const step = Math.min(remaining, maxStep);
      const shifted = shiftRecordAlongAxis(
        record,
        axis,
        direction,
        step,
        new Set(),
      );
      remaining -= shifted;
      if (shifted + EPSILON < step) break;
    }
  }
}

function candidatePositionsAround(
  moving: FloatingRecord,
  obstacle: FloatingRecord,
): FloatingPosition[] {
  const { gap } = getViewportMetrics();
  return [
    {
      x: obstacle.position.x + obstacle.width + gap,
      y: moving.position.y,
    },
    {
      x: obstacle.position.x - moving.width - gap,
      y: moving.position.y,
    },
    {
      x: moving.position.x,
      y: obstacle.position.y + obstacle.height + gap,
    },
    {
      x: moving.position.x,
      y: obstacle.position.y - moving.height - gap,
    },
  ];
}

function tryResolvePair(
  moving: FloatingRecord,
  obstacle: FloatingRecord,
  records: FloatingRecord[],
): boolean {
  const candidates = candidatePositionsAround(moving, obstacle)
    .map((position) => clampToViewport(position, moving.width, moving.height))
    .filter((position, index, all) =>
      all.findIndex(
        (candidate) =>
          Math.abs(candidate.x - position.x) < EPSILON &&
          Math.abs(candidate.y - position.y) < EPSILON,
      ) === index,
    )
    .map((position) => {
      const original = moving.position;
      moving.position = position;
      const clearsObstacle = !recordsOverlap(moving, obstacle);
      const secondaryOverlaps = records.filter(
        (record) =>
          record.instanceKey !== moving.instanceKey &&
          record.instanceKey !== obstacle.instanceKey &&
          recordsOverlap(moving, record),
      ).length;
      moving.position = original;
      return {
        position,
        clearsObstacle,
        secondaryOverlaps,
        distance: Math.hypot(
          position.x - original.x,
          position.y - original.y,
        ),
      };
    })
    .filter((candidate) => candidate.clearsObstacle)
    .sort(
      (left, right) =>
        left.secondaryOverlaps - right.secondaryOverlaps ||
        left.distance - right.distance,
    );

  const best = candidates[0];
  if (!best) return false;
  applyRecordPosition(moving, best.position);
  return true;
}

function resetToDefaultStack(records: FloatingRecord[]) {
  for (const record of records) {
    applyRecordPosition(
      record,
      clampToViewport(
        getDefaultPosition(record.stackIndex, record.width, record.height),
        record.width,
        record.height,
      ),
    );
  }
}

function resolveInvalidLayout(persist = true) {
  const records = getActiveRecords().sort(
    (left, right) => left.stackIndex - right.stackIndex,
  );
  if (records.length === 0) return;

  for (const record of records) {
    applyRecordPosition(
      record,
      clampToViewport(record.position, record.width, record.height),
    );
  }

  let unresolved = false;
  for (let pass = 0; pass < records.length * 6; pass += 1) {
    let changed = false;
    unresolved = false;

    for (let firstIndex = 0; firstIndex < records.length; firstIndex += 1) {
      for (
        let secondIndex = firstIndex + 1;
        secondIndex < records.length;
        secondIndex += 1
      ) {
        const first = records[firstIndex];
        const second = records[secondIndex];
        if (!recordsOverlap(first, second)) continue;
        unresolved = true;

        const preferred =
          activeDragInstance === first.instanceKey ? second : first;
        const obstacle = preferred === first ? second : first;
        if (
          tryResolvePair(preferred, obstacle, records) ||
          tryResolvePair(obstacle, preferred, records)
        ) {
          changed = true;
        }
      }
    }

    if (!unresolved || !changed) break;
  }

  if (unresolved) {
    resetToDefaultStack(records);
  }

  if (persist) flushDirtyPositions();
}

function measureAndResolveAll() {
  for (const record of floatingRegistry.values()) {
    if (!record.element) continue;
    const rect = record.element.getBoundingClientRect();
    record.width = rect.width || record.width || getViewportMetrics().expectedSize;
    record.height = rect.height || record.height || getViewportMetrics().expectedSize;
  }
  resolveInvalidLayout(true);
}

function scheduleLayoutResolution() {
  if (typeof window === "undefined" || layoutFrame !== null) return;
  layoutFrame = window.requestAnimationFrame(() => {
    layoutFrame = null;
    measureAndResolveAll();
  });
}

export function useDraggableFloating(
  storageKey: string,
  options: UseDraggableFloatingOptions = {},
) {
  const id = options.id ?? storageKey;
  const stackIndex = options.stackIndex ?? 0;
  const initialPositionRef = useRef<FloatingPosition | null>(null);
  const initialPosition =
    initialPositionRef.current ??
    getDefaultPosition(stackIndex);
  initialPositionRef.current = initialPosition;

  const [position, setPosition] = useState<FloatingPosition>(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const elementRef = useRef<HTMLButtonElement | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const suppressClickRef = useRef(false);
  const recordRef = useRef<FloatingRecord | null>(null);

  if (!recordRef.current) {
    recordRef.current = {
      instanceKey: Symbol(id),
      id,
      storageKey,
      stackIndex,
      element: null,
      position,
      width: getViewportMetrics().expectedSize,
      height: getViewportMetrics().expectedSize,
      setPosition,
    };
  }

  const record = recordRef.current;
  record.id = id;
  record.storageKey = storageKey;
  record.stackIndex = stackIndex;
  record.setPosition = setPosition;

  const setElementRef = useCallback(
    (element: HTMLButtonElement | null) => {
      const currentRecord = recordRef.current;
      if (!currentRecord) return;

      if (!element) {
        dirtyRecords.delete(currentRecord.instanceKey);
        floatingRegistry.delete(currentRecord.instanceKey);
        currentRecord.element = null;
        elementRef.current = null;
        return;
      }

      elementRef.current = element;
      currentRecord.element = element;
      const rect = element.getBoundingClientRect();
      currentRecord.width = rect.width || getViewportMetrics().expectedSize;
      currentRecord.height = rect.height || getViewportMetrics().expectedSize;
      currentRecord.position = clampToViewport(
        currentRecord.position,
        currentRecord.width,
        currentRecord.height,
      );
      floatingRegistry.set(currentRecord.instanceKey, currentRecord);
      currentRecord.setPosition(currentRecord.position);
      scheduleLayoutResolution();
    },
    [],
  );

  useEffect(() => {
    // Ignore and clean up coordinates saved by older app releases.
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // A disabled storage API must not affect the launcher.
    }
  }, [storageKey]);

  useEffect(() => {
    const handleResize = () => scheduleLayoutResolution();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      const currentRecord = recordRef.current;
      if (!currentRecord) return;
      dirtyRecords.delete(currentRecord.instanceKey);
      floatingRegistry.delete(currentRecord.instanceKey);
      if (activeDragInstance === currentRecord.instanceKey) {
        activeDragInstance = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isDragging || typeof document === "undefined") return;

    const root = document.documentElement;
    root.dataset.floatingDragging = "true";
    return () => {
      delete root.dataset.floatingDragging;
    };
  }, [isDragging]);

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) return;

      const currentRecord = recordRef.current;
      if (!currentRecord) return;
      const rect = event.currentTarget.getBoundingClientRect();
      currentRecord.width = rect.width || currentRecord.width;
      currentRecord.height = rect.height || currentRecord.height;
      currentRecord.position = clampToViewport(
        currentRecord.position,
        currentRecord.width,
        currentRecord.height,
      );

      dragStateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        pointerOffsetX: event.clientX - currentRecord.position.x,
        pointerOffsetY: event.clientY - currentRecord.position.y,
        dragging: false,
      };
      suppressClickRef.current = false;
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [],
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const dragState = dragStateRef.current;
      const currentRecord = recordRef.current;
      if (
        !dragState ||
        !currentRecord ||
        dragState.pointerId !== event.pointerId
      ) {
        return;
      }

      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;
      if (
        !dragState.dragging &&
        Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD
      ) {
        return;
      }

      if (!dragState.dragging) {
        dragState.dragging = true;
        suppressClickRef.current = true;
        activeDragInstance = currentRecord.instanceKey;
        setIsDragging(true);
      }

      event.preventDefault();
      const target = {
        x: event.clientX - dragState.pointerOffsetX,
        y: event.clientY - dragState.pointerOffsetY,
      };
      moveRecordToward(currentRecord, target);
    },
    [],
  );

  const finishDrag = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const dragState = dragStateRef.current;
      const currentRecord = recordRef.current;
      if (
        !dragState ||
        !currentRecord ||
        dragState.pointerId !== event.pointerId
      ) {
        return;
      }

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      dragStateRef.current = null;
      if (!dragState.dragging) return;

      activeDragInstance = null;
      setIsDragging(false);
      resolveInvalidLayout(false);
      flushDirtyPositions();
    },
    [],
  );

  const handleClickCapture = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      if (!suppressClickRef.current) return;
      suppressClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
    [],
  );

  const style: CSSProperties = {
    left: position.x,
    top: position.y,
    right: "auto",
    bottom: "auto",
    touchAction: "none",
    zIndex: isDragging ? "var(--layer-floating-elevated)" : undefined,
  };

  return {
    ref: setElementRef,
    style,
    isDragging,
    dragHandlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: finishDrag,
      onPointerCancel: finishDrag,
      onClickCapture: handleClickCapture,
    },
  };
}
